import { useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import i18n from '@/i18n'
import CustomCollectionsService from '@/services/CustomCollectionsServiceNew'
import SingleCustomCollectionService from '@/services/SingleCustomCollectionService'

const emptyLookupValue = { fieldName: '', fieldValue: '' }

const normalizeLookupValue = raw => {
	if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
		const fieldName = raw.fieldName ?? raw.value ?? raw.id ?? raw._id ?? ''
		const fieldValue = raw.fieldValue ?? raw.label ?? raw.name ?? raw.title ?? ''
		return {
			fieldName: fieldName ?? '',
			fieldValue: fieldValue === undefined || fieldValue === null ? '' : String(fieldValue),
		}
	}

	if (raw === undefined || raw === null || raw === '') return { ...emptyLookupValue }

	return {
		fieldName: raw,
		fieldValue: '',
	}
}

const getLookupConfig = field => {
	const lookupConfig = field.lookup || {}
	const collectionName =
		field.lookupCollectionName || lookupConfig.collectionName || lookupConfig.collection || null
	const collectionId = field.lookupCollectionId || lookupConfig.collectionId || null
	const linkField =
		field.lookupLinkField || lookupConfig.linkField || lookupConfig.collectionField || '_id'
	const displayField =
		field.lookupDisplayField ||
		lookupConfig.displayField ||
		lookupConfig.collectionField ||
		linkField ||
		'_id'

	return {
		collectionName,
		collectionId,
		linkField,
		displayField,
		extraFields: lookupConfig.extraFields || [],
		filters: lookupConfig.filters || {},
	}
}

const buildInitialFormData = details => {
	const initial = { collection: details?.name || '' }
	;(details?.fields || []).forEach(field => {
		if (field.name === 'collection' || field.name?.startsWith('_')) return
		if (field.type === 'lookup') {
			initial[field.name] = { ...emptyLookupValue }
			return
		}
		initial[field.name] = field.type === 'boolean' ? false : ''
	})
	return initial
}

const normalizeItemData = (item, details) => {
	const fields = details?.fields || []
	const normalized = { collection: details?.name || '' }

	fields.forEach(field => {
		if (field.name === 'collection' || field.name?.startsWith('_')) return
		const raw = item?.[field.name]
		if (raw === undefined || raw === null) {
			normalized[field.name] = field.type === 'boolean' ? false : field.type === 'lookup' ? { ...emptyLookupValue } : ''
			return
		}

		if (field.type === 'lookup') {
			normalized[field.name] = normalizeLookupValue(raw)
			return
		}

		if (field.type === 'date') {
			normalized[field.name] = moment(raw).isValid() ? moment(raw).format('YYYY-MM-DD') : ''
			return
		}

		normalized[field.name] = field.type === 'boolean' ? !!raw : raw
	})

	return normalized
}

export default function useFormData({ collectionId, itemId }) {
	const [collectionDetails, setCollectionDetails] = useState(null)
	const [formData, setFormData] = useState({})
	const [itemData, setItemData] = useState(null)
	const [lookupOptions, setLookupOptions] = useState({})
	const [lookupCollectionNames, setLookupCollectionNames] = useState({})
	const [loadingCollection, setLoadingCollection] = useState(true)
	const [loadingItem, setLoadingItem] = useState(false)
	const [loadError, setLoadError] = useState(null)
	const [deleting, setDeleting] = useState(false)
	const [deleteError, setDeleteError] = useState(null)

	// Preloading collection details
	useEffect(() => {
		const loadCollection = async () => {
			if (!collectionId) {
				setLoadError(i18n.t('singleCustomCollection.list.messages.collectionIdRequired'))
				setLoadingCollection(false)
				setCollectionDetails(null)
				setFormData({})
				setItemData(null)
				return
			}

			setLoadingCollection(true)
			setLoadError(null)

			try {
				const result = await CustomCollectionsService.getCustomCollectionById(collectionId)
				if (result.error) {
					setLoadError(result.error)
					setCollectionDetails(null)
					setFormData({})
					setItemData(null)
					return
				}

				setCollectionDetails(result.data)
				setFormData(buildInitialFormData(result.data))
				setItemData(null)
			} catch (err) {
				console.error('Failed to load collection details:', err)
				setLoadError(i18n.t('singleCustomCollection.details.messages.loadCollectionFailed'))
				setCollectionDetails(null)
				setFormData({})
				setItemData(null)
			} finally {
				setLoadingCollection(false)
			}
		}

		loadCollection()
	}, [collectionId])

	// Preloading item details
	useEffect(() => {
		const loadItem = async () => {
			if (!collectionDetails || !itemId) {
				setLoadingItem(false)
				setItemData(null)
				return
			}

			setLoadingItem(true)
			setLoadError(null)

			try {
				const result = await SingleCustomCollectionService.getCustomCollectionItemById(collectionDetails.name,itemId)
				// console.log(`TCL -> useFormData.js:158 -> loadItem -> result:`, result)

				if (result.error) {
					setLoadError(result.error)
					setFormData(buildInitialFormData(collectionDetails))
					setItemData(null)
					return
				}

				setItemData(result.data)
				setFormData(normalizeItemData(result.data, collectionDetails))
			} catch (err) {
				console.error('Failed to load item data:', err)
				setLoadError(i18n.t('singleCustomCollection.details.messages.loadItemFailed'))
				setFormData(buildInitialFormData(collectionDetails))
				setItemData(null)
			} finally {
				setLoadingItem(false)
			}
		}

		loadItem()
	}, [collectionDetails, itemId])

	const setFieldValue = (name, value) => { setFormData(prev => ({...prev,[name]: value}))}

	const deleteItem = async customItemId => {
		const resolvedCollectionName = collectionDetails?.name || formData?.collection || collectionId || null
		const resolvedItemId = customItemId || itemId || itemData?._id || null

		if (!resolvedCollectionName || !resolvedItemId) {
			const message = i18n.t('singleCustomCollection.details.messages.deleteIdentifiersRequired')
			setDeleteError(message)
			return { success: false, error: message }
		}

		setDeleting(true)
		setDeleteError(null)

		try {
			const result = await SingleCustomCollectionService.deleteCustomCollectionItem(
				resolvedCollectionName,
				resolvedItemId
			)

			if (!result.success) {
				const message = result.error || i18n.t('singleCustomCollection.details.messages.deleteFailed')
				setDeleteError(message)
				return { success: false, error: message }
			}

			setItemData(null)
			setFormData(collectionDetails ? buildInitialFormData(collectionDetails) : {})
			return { success: true, error: null, message: result.message }
		} catch (err) {
			const message = err.message || i18n.t('singleCustomCollection.details.messages.deleteFailed')
			setDeleteError(message)
			return { success: false, error: message }
		} finally {
			setDeleting(false)
		}
	}

	const displayFields = useMemo(
		() =>
			collectionDetails?.fields?.filter(field => field.name !== 'collection' && !field.name?.startsWith('_')) || [],
		[collectionDetails]
	)

	return {
		collectionDetails,
		displayFields,
		formData,
		itemData,
		lookupOptions,
		lookupCollectionNames,
		loadingCollection,
		loadingItem,
		loadError,
		deleteError,
		deleting,
		setFieldValue,
		setLoadError,
		setDeleteError,
		setFormData,
		deleteItem,
	}
}
